import { useEffect, useRef } from "react";

// feat_346 — re-sync al despertar (suspensión / pestaña dormida).
//
// El disparador y el debounce viven en `services/socket-io.js`: ahí se sabe
// cuándo un socket RE-conectó (no la primera vez) y ahí se colapsan los ~13
// sockets de la pestaña en UN solo evento `waia:resync`. Este hook es el
// consumidor: escucha ese evento, aplica el gate de tiempo y llama al callback.
//
// ⚠ El callback tiene que hacer RESET + re-fetch, NUNCA re-fetch a secas:
// `LOAD_TICKETS` fusiona y pushea al FINAL (sort roto) y `LOAD_MESSAGES`
// prependea (orden roto). Ver el recon, §3.2 y §4.2.
const RESYNC_EVENT = "waia:resync";

// Un alt-tab de 3 segundos no debe tirar abajo el historial ya cargado del
// chat. 30 s es el piso para considerar que la pestaña estuvo "dormida".
const DEFAULT_MIN_HIDDEN_MS = 30000;

const useResyncOnResume = (callback, { minHiddenMs = DEFAULT_MIN_HIDDEN_MS } = {}) => {
  // Ref para que el listener no se re-suscriba en cada render (el callback
  // suele ser una arrow nueva por render).
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const hiddenSinceRef = useRef(null);
  const lastHiddenMsRef = useRef(0);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
        return;
      }
      if (hiddenSinceRef.current) {
        lastHiddenMsRef.current = Date.now() - hiddenSinceRef.current;
        hiddenSinceRef.current = null;
      }
    };

    const handleResync = event => {
      const detail = (event && event.detail) || {};
      // El servicio ya midió el tiempo oculto; el fallback local cubre el caso
      // de un evento sin detail (o disparado por otra vía en el futuro).
      const hiddenMs =
        typeof detail.hiddenMs === "number" && detail.hiddenMs > 0
          ? detail.hiddenMs
          : lastHiddenMsRef.current;
      const socketDied = !!detail.socketDied;

      // Gate: resincroniza si la pestaña estuvo dormida lo suficiente O si el
      // socket se cayó (ahí SÍ se perdieron eventos, aunque la pestaña estuviera
      // visible: corte de wifi). Alt-tab corto + socket sano ⇒ no se toca nada.
      if (!socketDied && hiddenMs < minHiddenMs) return;

      lastHiddenMsRef.current = 0;
      callbackRef.current();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener(RESYNC_EVENT, handleResync);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(RESYNC_EVENT, handleResync);
    };
  }, [minHiddenMs]);
};

export default useResyncOnResume;
